import React from 'react';

import { useViewport } from '../../../design-system/hooks/useViewport';
import { Segmented } from '../../../design-system/components/desktop';
import { SegmentedControl, SegmentedControlItem } from '../../../design-system/components/mobile';
import {
  ModalError,
  ModalField,
  ModalFields,
  ModalForm,
  ModalGroup,
  ModalInput,
  ModalNote,
  ModalReadout,
  ModalSelect,
  ModalTextarea,
} from '../../../design-system/patterns/modal-form';
import { ImageUpload } from '../../../design-system/patterns/upload';

/**
 * Edit listing. The view. Pure.
 *
 * The last surface of the conversion, and the last `GradientButton` in the app.
 * What went: the legacy `UI/Modal`, `CustomSelect`, `OutLinedButton`, the
 * gradient submit, the hand-rolled `.form-group` stack, the `.toggle-btn` pair
 * with its accent fill, the `.currency-symbol` absolute overlay and the
 * `.error-text` span. What stayed: every field, every label, every validation
 * message and every value the form collects, so the multipart body the seller
 * submits is byte for byte what it was.
 *
 * THE TWO MODE TOGGLES. A pricing mode is a committed choice, so the chosen
 * segment fills with INK, never accent. `Segmented` is desktop-only and
 * `SegmentedControl` is the mobile track's equivalent, so `useViewport()` picks
 * one and only one mounts: a `dsk-` class must never land in a `ds-` tree.
 *
 * WHAT IS A READOUT AND NOT A FIELD. The platform fee and the derived buyer
 * price were disabled inputs. A control that can never be operated is not a
 * control, so they are `ModalReadout` figures now. Nothing about what is
 * submitted changes: the buyer price was never a payload key, the container
 * computes it and the backend recomputes it authoritatively on save.
 *
 * Prices are RANDS. The `R` is carried by the field label and by the readout
 * value, never injected into the value a control holds, so `formData.price`
 * stays a plain number string.
 *
 * THE IMAGE PICKER, AND WHY THIS FILE NO LONGER HAS A STYLESHEET
 * -------------------------------------------------------------
 * `ModalFile` plus a local `Thumb` is now `patterns/upload`: a real drop zone,
 * the recommended size stated on it, and the same removable previews. The
 * thumbnail strip was the ONLY thing left in `EditListingModal.css`, and a
 * second surface now shows previews, which is the promotion test in
 * `patterns/README.md`, so the strip moved into `upload.css` and the file went
 * with it. Same 88px box, same 24px remove button, same tokens — a move.
 *
 * The payload did not move. `onFilesSelected` still receives `e.target.files`,
 * the container still appends existing URLs then new Files under `images`, and
 * the five-image ceiling is still the container's rule and its message.
 */

/** The two mode toggles, track-neutral. One track mounts, never both. */
function ModeToggle({ label, value, onChange, items }) {
  const { isDesktop } = useViewport();

  if (isDesktop) {
    return <Segmented label={label} value={value} onChange={onChange} items={items} />;
  }

  return (
    <SegmentedControl role="group" aria-label={label}>
      {items.map((item) => (
        <SegmentedControlItem
          key={item.value}
          active={item.value === value}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </SegmentedControlItem>
      ))}
    </SegmentedControl>
  );
}

export function EditListingModalView({
  open,
  cardName,
  conditionOptions = [],
  condition,
  onConditionChange,
  description,
  pricingMode,
  onPricingModeChange,
  feeFrame,
  onFeeFrameChange,
  markupPercent,
  onMarkupChange,
  priceFloor,
  onFloorChange,
  price,
  onFixedValueChange,
  platformFee,
  buyer,
  breakdown,
  quantity,
  boughtForPrice,
  address,
  processingDays,
  existingImages = [],
  newImages = [],
  onInputChange,
  onFilesSelected,
  onRemoveExistingImage,
  onRemoveNewImage,
  errors = {},
  submitting = false,
  onClose,
  onSubmit,
}) {
  const remaining = Math.max(0, 5 - existingImages.length - newImages.length);

  // Saved images first, then the ones picked in this session, which is the order
  // the container appends them to the FormData. Keeping the two lists distinct
  // matters: they have different remove handlers, because removing a saved image
  // drops a URL from the payload and removing a new one drops a File.
  const thumbs = [
    ...existingImages.map((url, index) => ({
      key: `existing-${index}`,
      url,
      alt: `Current listing image ${index + 1}`,
      caption: 'Saved',
      removeLabel: `Remove current image ${index + 1}`,
      onRemove: () => onRemoveExistingImage(index),
    })),
    ...newImages.map((item, index) => ({
      key: `new-${index}`,
      url: item.url,
      alt: item.name || `New image ${index + 1}`,
      caption: 'New',
      removeLabel: `Remove new image ${index + 1}`,
      onRemove: () => onRemoveNewImage(index),
    })),
  ];

  return (
    <ModalForm
      open={open}
      title="Edit listing"
      subtitle="Update your card listing details"
      onClose={onClose}
      onSubmit={onSubmit}
      submitting={submitting}
      submitLabel="Update listing"
      submittingLabel="Updating"
      error={errors.submit}
    >
      <ModalFields cols={2}>
        <ModalField label="Card name">
          <ModalInput value={cardName} locked placeholder="Card name" />
        </ModalField>

        <ModalField label="Condition" error={errors.condition}>
          <ModalSelect
            options={conditionOptions}
            value={condition}
            onChange={onConditionChange}
            placeholder="Select condition"
          />
        </ModalField>

        <ModalField label="Description" error={errors.description} full>
          <ModalTextarea
            name="description"
            value={description}
            onChange={onInputChange}
            placeholder="Enter card description"
            rows={3}
          />
        </ModalField>
      </ModalFields>

      <ModalGroup title="Pricing">
        <ModalField label="Pricing mode">
          <ModeToggle
            label="Pricing mode"
            value={pricingMode}
            onChange={onPricingModeChange}
            items={[
              { value: 'fixed', label: 'Fixed price' },
              { value: 'tracked', label: 'Market-tracked' },
            ]}
          />
        </ModalField>

        <ModalField label="Fee frame">
          <ModeToggle
            label="Fee frame"
            value={feeFrame}
            onChange={onFeeFrameChange}
            items={[
              { value: 'inclusive', label: 'Incl. fee' },
              { value: 'exclusive', label: 'Excl. fee' },
            ]}
          />
        </ModalField>

        {pricingMode === 'tracked' ? (
          <>
            <ModalFields cols={2}>
              <ModalField label="Markup %" error={errors.markup_percent}>
                <ModalInput
                  type="text"
                  inputMode="decimal"
                  value={markupPercent}
                  placeholder="0"
                  onChange={onMarkupChange}
                />
              </ModalField>
              <ModalField label="Price floor (R)">
                <ModalInput
                  type="text"
                  inputMode="decimal"
                  value={priceFloor}
                  placeholder="0.00"
                  onChange={onFloorChange}
                />
              </ModalField>
            </ModalFields>
            <ModalError>{errors.price}</ModalError>
          </>
        ) : (
          <ModalField label="Price (R)" error={errors.price}>
            <ModalInput
              type="text"
              inputMode="decimal"
              value={price}
              placeholder="0.00"
              onChange={onFixedValueChange}
            />
          </ModalField>
        )}

        <ModalReadout label="Platform fee" value={`${platformFee} %`} />
        <ModalReadout
          label="Buyer price"
          value={buyer != null ? `R${buyer.toFixed(2)}` : 'No price'}
        />

        {pricingMode === 'tracked' && buyer === null ? (
          <ModalNote>No market price, so use a fixed price.</ModalNote>
        ) : (
          <ModalNote>
            Buyer pays R{breakdown.buyer.toFixed(2)} · Platform fee R{breakdown.fee.toFixed(2)} ·
            You receive R{breakdown.net.toFixed(2)}
          </ModalNote>
        )}

        <ModalField label="What you paid (R)" optional>
          <ModalInput
            name="bought_for_price"
            type="number"
            step="0.01"
            value={boughtForPrice}
            onChange={onInputChange}
            placeholder="Enter the price you paid for this card"
          />
        </ModalField>
        <ModalNote>
          Enter what you paid for this card so your profit figure is real. Leaving it blank does
          not block the update, it only makes that figure a guess.
        </ModalNote>
      </ModalGroup>

      <ModalGroup title="Stock and dispatch">
        <ModalFields cols={2}>
          <ModalField label="Quantity" error={errors.quantity}>
            <ModalInput
              name="quantity"
              type="number"
              min="1"
              value={quantity}
              onChange={onInputChange}
              placeholder="Enter quantity"
            />
          </ModalField>

          <ModalField label="Processing days" error={errors.processing_days}>
            <ModalInput
              name="processing_days"
              type="number"
              min="1"
              value={processingDays}
              onChange={onInputChange}
              placeholder="Enter processing days"
            />
          </ModalField>

          <ModalField label="Address" error={errors.address} full>
            <ModalInput
              name="address"
              value={address}
              onChange={onInputChange}
              placeholder="Enter address"
            />
          </ModalField>
        </ModalFields>
      </ModalGroup>

      <ModalGroup title="Images">
        <ModalField label="Add images" error={errors.images} optional>
          {/* WHERE 1500 COMES FROM. A listing photo's biggest render is the
              detail page's `.dsa-gallery__hero`, a 4:3 box in the 3fr column of
              the 1280px `--dsk-container` less its 40px gap: 744 CSS px wide.
              Doubled for a 2x screen that is 1488, rounded to 1500. Every other
              render is smaller — a 72px gallery thumb, a card tile around 208px
              — so the hero is the only one worth sizing for.

              The zone is NOT disabled when the five-image limit is reached. The
              container owns that rule and answers with its own message; taking
              the affordance away here would leave that message unreachable and
              put the count in two places at once. */}
          <ImageUpload
            label="Add images"
            multiple
            accept="image/*"
            recommended="4:3, 1500 × 1125 px or larger"
            onFilesSelected={onFilesSelected}
            items={thumbs}
            note={
              remaining > 0
                ? `You can add ${remaining} more, five images in total.`
                : 'Five images is the limit. Remove one to add another.'
            }
          />
        </ModalField>
      </ModalGroup>
    </ModalForm>
  );
}

export default EditListingModalView;
